import { Link } from 'react-router-dom'
import { Icon } from '../ui/Icon'
import { Logo } from '../ui/Logo'

export interface NavItem {
  id: string
  icon: string
  name: string
  count?: number
  href: string
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export interface WorkspaceChip {
  initials: string
  name: string
  sub: string
  /** Where the chip goes when clicked. Omitted renders it as a static label. */
  href?: string
}

export interface SidebarProps {
  groups: NavGroup[]
  activeHref: string
  workspace?: WorkspaceChip
  /** Icons only. Each row keeps its name as a tooltip and an accessible label. */
  collapsed?: boolean
}

/**
 * Ported from the Admin Console's Sidebar. Geometry, active-row tint and count pills are its
 * CSS unchanged; the ONE change is `next/link` becoming react-router's `Link`, because this
 * app is Vite and that one is Next. Rows stay real links so every page is deep linkable.
 */
export function Sidebar({ groups, activeHref, workspace, collapsed = false }: SidebarProps) {
  return (
    <aside className="sidebar">
      <div className="brand">
        <Logo size={30} />
        <div className="name">
          NthAI<small>· Fleet Console</small>
        </div>
      </div>

      {groups.map((group) => (
        <div key={group.label}>
          <div className="group-label">{group.label}</div>
          <nav>
            {group.items.map((item) => {
              const active = item.href === activeHref
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`nav-item ${active ? 'active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  // Collapsed, the name is not on screen, so it moves to the tooltip and the label.
                  title={collapsed ? item.name : undefined}
                  aria-label={collapsed ? item.name : undefined}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.name}</span>
                  {item.count != null && <span className="count">{item.count}</span>}
                </Link>
              )
            })}
          </nav>
        </div>
      ))}

      {workspace && (
        <div className="footer">
          <div className="ws" title={collapsed ? `${workspace.name} · ${workspace.sub}` : undefined}>
            <div className="ava">{workspace.initials}</div>
            <div className="meta">
              <div className="t">{workspace.name}</div>
              <div className="s">{workspace.sub}</div>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}
